import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { AccordionSection, H3, SplitChecksToggleBreakdown, ToggleableBreakdown } from 'components';
import IgnoredChecksBreakdown from 'components/Calculator/IgnoredChecksBreakdown/IgnoredChecksBreakdown';
import { Tag } from 'components/Tag';
import { Text } from 'components/Typography';
import { theme, toRem } from 'design';
import { Size } from 'design/types';
import { selectSiteSettings } from 'store/Site';
import { selectCalculatorResults } from 'store/TipCalculator';
import { ICalculatorRule, ICategoryGroup, IPOSData } from 'types';
import { buildCalculatedResultsForEmployee, buildEmployeeOverrideTicketBreakdown, centsToDollarsDisplay, sortAlphaNumerically, toTitleCase } from 'utils';

interface ISharedPOSBreakdownProps {
  calculatorRules: ICalculatorRule[];
  categoryGroups: ICategoryGroup[];
  dataCy?: string;
  posData: IPOSData;
}

const HeaderContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
`;

const StyledTag = styled(Tag)`
  background-color: ${theme.color.neutral[200]};
  margin-bottom: ${toRem(4)};
`;

const HeaderLabel = styled(Text)`
  color: ${theme.color.neutral[500]};
  margin-bottom: ${toRem(4)};
`;

const HeaderTotal = styled(Text)`
  font-size: ${toRem(14)};
`;

const POSBreakdown = styled.div`
  display: flex;
  flex-flow: column nowrap;
  background-color: ${theme.color.neutral[50]};
  width: 100%;
  padding: 0 ${toRem(16)} ${toRem(16)} ${toRem(16)};

  > div {
    &:not(:first-child) {
      margin-top: ${toRem(16)};
    }
  }
`;

const SharedPOSBreakdown = ({ calculatorRules, categoryGroups, posData, dataCy = 'shared-pos-list-item' }: ISharedPOSBreakdownProps) => {
  const tipCalcResults = useSelector(selectCalculatorResults());
  const calculatedSharedPOSResults = buildCalculatedResultsForEmployee(posData.provider.employeeId, tipCalcResults);
  const { tipCalculatorPercentFee: creditCardFee } = useSelector(selectSiteSettings()) || {};

  const collectedPOSTotal = useMemo(() => posData.segments?.reduce((acc, segment) => acc + segment.totals.gross, 0), [posData]);

  const renderSegmentBreakdown = useMemo(
    () =>
      posData.segments?.map((segment, idx) => {
        const calculatedSegmentResult = calculatedSharedPOSResults.find((calculation) => calculation.segmentId === segment.id);

        if (!calculatedSegmentResult) return null;

        return (
          <ToggleableBreakdown
            key={`contributing-breakdown-${segment.id}-${idx}`}
            dataCy="shared-pos-breakdown"
            calculatorRules={calculatorRules}
            categoryGroups={categoryGroups}
            calculation={calculatedSegmentResult}
            creditCardFee={creditCardFee}
            segmentName={segment.name}
            contributingOnly
          />
        );
      }),
    [posData]
  );

  const renderOverrideBreakdown = useMemo(() => {
    const calculatedSegmentResult = posData.segments?.flatMap((segment) => calculatedSharedPOSResults.filter((calculation) => calculation.segmentId === segment.id));
    const { splitChecks, ignoredChecks } = buildEmployeeOverrideTicketBreakdown(calculatedSegmentResult);

    return (
      <>
        {(!!splitChecks.received?.length || !!splitChecks.contributions?.length) && <SplitChecksToggleBreakdown splitChecks={splitChecks} contributingOnly />}
        {!!ignoredChecks.length && <IgnoredChecksBreakdown ignoredCheckData={ignoredChecks.sort(sortAlphaNumerically('displayId'))} />}
      </>
    );
  }, [posData]);

  return (
    <AccordionSection
      key={`shared-pos-${posData.provider.employeeId}`}
      dataCy={dataCy}
      open={false}
      compact
      title={
        <HeaderContainer data-cy="shared-pos-header">
          <StyledTag label="Shared POS" size={Size.SMALL} />
          <H3>{toTitleCase(posData.provider.name)}</H3>
        </HeaderContainer>
      }
      actions={
        <HeaderContainer data-cy="shared-pos-collected-total">
          <HeaderLabel size="xxsmall" fontWeight="medium" color={theme.color.neutral[500]}>
            Collected
          </HeaderLabel>
          <HeaderTotal fontWeight="medium">{centsToDollarsDisplay(collectedPOSTotal)}</HeaderTotal>
        </HeaderContainer>
      }
    >
      <POSBreakdown>
        {renderSegmentBreakdown}
        {renderOverrideBreakdown}
      </POSBreakdown>
    </AccordionSection>
  );
};

export default SharedPOSBreakdown;
